import apiClient from '../config/api';

const workOrderService = {
  /**
   * Get all work orders
   * @param {object} filters - Filter options (machineId, workOrderType, statusId, startDate, endDate)
   * @returns {Promise} Array of work orders
   */
  getWorkOrders: async (filters = {}) => {
    const params = {};
    if (filters.machineId) params.machineId = filters.machineId;
    if (filters.workOrderType) params.workOrderType = filters.workOrderType;
    if (filters.statusId) params.statusId = filters.statusId;
    if (filters.startDate) params.startDate = filters.startDate.toISOString();
    if (filters.endDate) params.endDate = filters.endDate.toISOString();
    
    const response = await apiClient.get('/workorder', { params });
    return response.data;
  },

  /**
   * Get work order by ID
   * @param {number} workOrderId - Work order ID
   * @returns {Promise} Work order details
   */
  getWorkOrderById: async (workOrderId) => {
    const response = await apiClient.get(`/workorder/${workOrderId}`);
    return response.data;
  },

  /**
   * Get work orders by machine
   * @param {number} machineId - Machine ID
   * @param {Date} startDate - Optional start date filter
   * @param {Date} endDate - Optional end date filter
   * @returns {Promise} Array of work orders
   */
  getWorkOrdersByMachine: async (machineId, startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    
    const response = await apiClient.get(`/workorder/machine/${machineId}`, { params });
    return response.data;
  },

  /**
   * Get work orders by type
   * @param {string} type - Work order type (Breakdown, Corrective, Preventive)
   * @param {Date} startDate - Optional start date filter
   * @param {Date} endDate - Optional end date filter
   * @returns {Promise} Array of work orders
   */
  getWorkOrdersByType: async (type, startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    
    const response = await apiClient.get(`/workorder/type/${type}`, { params });
    return response.data;
  },

  /**
   * Create work order
   * @param {object} workOrderData - Work order data
   * @returns {Promise} Created work order
   */
  createWorkOrder: async (workOrderData) => {
    const response = await apiClient.post('/workorder', workOrderData);
    return response.data;
  },

  /**
   * Create work order from breakdown
   * @param {number} breakdownLogId - Breakdown log ID
   * @param {object} workOrderData - Work order data
   * @returns {Promise} Created work order
   */
  createWorkOrderFromBreakdown: async (breakdownLogId, workOrderData) => {
    const response = await apiClient.post(`/workorder/from-breakdown/${breakdownLogId}`, workOrderData);
    return response.data;
  },

  /**
   * Update work order
   * @param {number} workOrderId - Work order ID
   * @param {object} workOrderData - Updated work order data
   * @returns {Promise} Updated work order
   */
  updateWorkOrder: async (workOrderId, workOrderData) => {
    const response = await apiClient.post(`/workorder/update/${workOrderId}`, workOrderData);
    return response.data;
  },

  /**
   * Update work order status
   * @param {number} workOrderId - Work order ID
   * @param {number} statusId - New status ID
   * @returns {Promise} Updated work order
   */
  updateWorkOrderStatus: async (workOrderId, statusId) => {
    const response = await apiClient.post(`/workorder/update/${workOrderId}/status`, { statusId });
    return response.data;
  },

  /**
   * Delete work order
   * @param {number} workOrderId - Work order ID
   * @returns {Promise} Deletion result
   */
  deleteWorkOrder: async (workOrderId) => {
    const response = await apiClient.post(`/workorder/delete/${workOrderId}`);
    return response.data;
  },
};

export default workOrderService;

